<template>
  <div class="Component video-wrapper-container" :data-video-size="videoSize" :data-id="videoId" v-if="videoId">
    <h3 v-if="videoTitleHtml" v-html="videoTitleHtml"></h3>
    <div class="video-box" :style="{ backgroundImage: `url(${thumbnailUrl})` }" @click.prevent="handleVideoClick">
      <a class="video-play-icon clickVideo" href="javascript:void(0)">
        <svg xmlns="http://www.w3.org/2000/svg" width="98" height="98" viewBox="0 0 98 98" fill="none">
          <circle cx="49" cy="48" r="45" fill="white"/>
          <path d="M68.0498 46.268C69.3831 47.0378 69.3831 48.9622 68.0498 49.732L40.2998 65.7535C38.9665 66.5233 37.2998 65.5611 37.2998 64.0215L37.2998 31.9785C37.2998 30.4389 38.9665 29.4767 40.2998 30.2465L68.0498 46.268Z" fill="#252836"/>
        </svg>
      </a>
    </div>

    <!-- Video Modal -->
    <transition name="modal-fade">
      <div class="video-modal" v-if="showModal" @click="closeModal">
        <div class="modal-content" @click.stop>
          <button class="close-button" @click="closeModal">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M25 7L7 25M7 7L25 25" stroke="white" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </button>
          <div class="video-container">
            <div :id="jwPlayerId" ref="jwPlayerContainer"></div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { renderPrismicContent, getCleanPrismicImageUrl } from '../helpers/prismicHelpers';

export default {
  name: 'VideoWrapper',
  props: {
    sliceData: {
      type: Object,
      required: true
    },
    prismicLang: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      showModal: false,
      jwPlayerInstance: null
    };
  },
  computed: {
    videoId() {
      return this.sliceData?.primary?.jw_id || '';
    },
    jwPlayerId() {
      return `jwplayer-${this.videoId}`;
    },
    videoTitleHtml() {
      const titleArray = this.sliceData?.primary?.video_title;
      if (!titleArray || !Array.isArray(titleArray) || titleArray.length === 0) {
        return '';
      }
      return renderPrismicContent(titleArray);
    },
    thumbnailUrl() {
      const url = this.sliceData?.primary?.video_thumbnail?.url;
      return getCleanPrismicImageUrl(url) || 'https://placehold.co/1828x1024';
    },
    videoSize() {
      // Prismic config: 100% / 75% / 50% / 25%
      const size = this.sliceData?.primary?.size || '100%';
      return `width-${size.replace('%', '')}`;
    }
  },
  methods: {
    handleVideoClick() {
      if (this.videoId) {
        this.showModal = true;
        document.body.style.overflow = 'hidden';
        // Wait for DOM to update, then initialize JWPlayer
        this.$nextTick(() => {
          this.initJWPlayer();
        });
      }
    },
    initJWPlayer() {
      // Destroy existing instance if any
      this.destroyJWPlayer();
      
      // Check if jwplayer is available globally
      if (typeof window.jwplayer === 'undefined') {
        console.error('JWPlayer library not loaded');
        return;
      }

      // Initialize JWPlayer with the media ID
      this.jwPlayerInstance = window.jwplayer(this.jwPlayerId).setup({
        playlist: `https://cdn.jwplayer.com/v2/media/${this.videoId}`,
        width: '100%',
        aspectratio: '16:9',
        autostart: true
      });
    },
    destroyJWPlayer() {
      if (this.jwPlayerInstance) {
        try {
          this.jwPlayerInstance.remove();
        } catch (e) {
          console.warn('Error destroying JWPlayer instance:', e);
        }
        this.jwPlayerInstance = null;
      }
    },
    closeModal() {
      this.destroyJWPlayer();
      this.showModal = false;
      document.body.style.overflow = '';
    }
  },
  beforeDestroy() {
    // Cleanup: destroy player and restore body overflow
    this.destroyJWPlayer();
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
.video-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
}

.modal-content {
  position: relative;
  width: 100%;
  max-width: 1200px;
  aspect-ratio: 16 / 9;
}

.close-button {
  position: absolute;
  top: -50px;
  right: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 8px;
  transition: transform 0.2s ease;
  z-index: 10;
}

.close-button:hover {
  transform: scale(1.1);
}

.close-button svg {
  display: block;
}

.video-container {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
}

/* JWPlayer container styles */
.video-container >>> .jwplayer {
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100% !important;
  height: 100% !important;
}

/* Modal transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .video-modal {
    padding: 10px;
  }

  .close-button {
    top: -40px;
    right: -10px;
  }

  .close-button svg {
    width: 24px;
    height: 24px;
  }
}
</style>
